import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Clock, Save, Send, Check, X, RefreshCw, Calendar, Minus, AlertCircle } from 'lucide-react';
import { format, parseISO, eachDayOfInterval, addDays } from 'date-fns';
import { toast } from 'sonner';

export default function Timesheets() {
  const { api, isAdmin, user } = useAuth();
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [myTimesheet, setMyTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Admin review
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [breakAdjustments, setBreakAdjustments] = useState({});
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchTimesheets();
    }
  }, [selectedPeriod]);

  const fetchPayPeriods = async () => {
    try {
      const response = await api.get('/timesheets/pay-periods');
      setPayPeriods(response.data);
      if (response.data.length > 0) {
        setSelectedPeriod(response.data[0]);
      }
    } catch (error) {
      console.error('Pay periods fetch error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch pay periods';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const response = await api.get('/timesheets', {
        params: { pay_period_id: selectedPeriod.id }
      });
      
      if (isAdmin) {
        setTimesheets(response.data);
      } else {
        const mine = response.data.find(t => t.user_id === user.id);
        setMyTimesheet(mine);
        initializeEntries(mine);
      }
    } catch (error) {
      toast.error('Failed to fetch timesheets');
    }
  };

  const initializeEntries = (existingTimesheet) => {
    const start = parseISO(selectedPeriod.start_date);
    const end = parseISO(selectedPeriod.end_date);
    const days = eachDayOfInterval({ start, end });

    const newEntries = days.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const existing = existingTimesheet?.entries?.find(e => e.date === dateStr);
      
      return {
        date: dateStr,
        day: format(date, 'EEE'),
        start_time: existing?.start_time || '',
        end_time: existing?.end_time || '',
        break_minutes: existing?.break_minutes || 0,
        hours: existing?.hours || 0,
        notes: existing?.notes || ''
      };
    });

    setEntries(newEntries);
  };

  const calculateHours = (startTime, endTime, breakMinutes) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const hours = diffMs / (1000 * 60 * 60) - (breakMinutes / 60);
    return Math.max(0, Math.round(hours * 100) / 100);
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    // Recalculate hours
    if (['start_time', 'end_time', 'break_minutes'].includes(field)) {
      newEntries[index].hours = calculateHours(
        newEntries[index].start_time,
        newEntries[index].end_time,
        parseInt(newEntries[index].break_minutes) || 0
      );
    }
    
    setEntries(newEntries);
  };

  const getTotalHours = () => {
    return entries.reduce((sum, e) => sum + (e.hours || 0), 0).toFixed(2);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validEntries = entries.filter(e => e.start_time && e.end_time);
      if (validEntries.length === 0) {
        toast.error('Please add at least one time entry before saving');
        setSaving(false);
        return;
      }
      await api.post('/timesheets', {
        pay_period_id: selectedPeriod.id,
        entries: validEntries
      });
      toast.success('Timesheet saved');
      fetchTimesheets();
    } catch (error) {
      console.error('Timesheet save error:', error);
      let errorMsg = 'Failed to save timesheet';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message === 'Network Error') {
        errorMsg = 'Unable to reach server. Check your connection.';
      } else if (error.response?.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'You do not have permission to perform this action.';
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!myTimesheet) {
      await handleSave();
      await new Promise(r => setTimeout(r, 500));
      await fetchTimesheets();
    }
    
    setSaving(true);
    try {
      const response = await api.get('/timesheets', {
        params: { pay_period_id: selectedPeriod.id }
      });
      const mine = response.data.find(t => t.user_id === user.id);
      
      if (mine) {
        await api.post(`/timesheets/${mine.id}/submit`);
        toast.success('Timesheet submitted for approval');
        fetchTimesheets();
      } else {
        toast.error('Please save your timesheet first before submitting');
      }
    } catch (error) {
      console.error('Timesheet submit error:', error);
      let errorMsg = 'Failed to submit timesheet';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message === 'Network Error') {
        errorMsg = 'Unable to reach server. Check your connection.';
      } else if (error.response?.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMsg = error.response.data?.detail || 'You do not have permission to submit this timesheet.';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data?.detail || 'Timesheet cannot be submitted in its current state.';
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (timesheetId) => {
    setApproving(true);
    try {
      // Check if there are any break adjustments
      const adjustments = Object.entries(breakAdjustments)
        .filter(([_, minutes]) => minutes > 0)
        .map(([date, minutes]) => ({
          date,
          additional_break_minutes: parseInt(minutes)
        }));

      if (adjustments.length > 0) {
        // Use approve-with-adjustments endpoint
        await api.post(`/timesheets/${timesheetId}/approve-with-adjustments`, {
          break_adjustments: adjustments,
          adjustment_notes: adjustmentNotes
        });
        toast.success('Timesheet approved with break deductions');
      } else {
        // Use regular approve endpoint
        await api.post(`/timesheets/${timesheetId}/approve`);
        toast.success('Timesheet approved');
      }
      
      fetchTimesheets();
      setReviewDialogOpen(false);
      setBreakAdjustments({});
      setAdjustmentNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (timesheetId) => {
    if (!rejectionComment.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`/timesheets/${timesheetId}/reject?comment=${encodeURIComponent(rejectionComment)}`);
      toast.success('Timesheet rejected');
      fetchTimesheets();
      setReviewDialogOpen(false);
      setRejectionComment('');
    } catch (error) {
      toast.error('Failed to reject timesheet');
    }
  };

  const openReviewDialog = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setRejectionComment('');
    setBreakAdjustments({});
    setAdjustmentNotes('');
    setReviewDialogOpen(true);
  };

  const updateBreakAdjustment = (date, minutes) => {
    setBreakAdjustments(prev => ({
      ...prev,
      [date]: Math.max(0, parseInt(minutes) || 0)
    }));
  };

  const getAdjustedTotal = () => {
    if (!selectedTimesheet) return 0;
    let total = selectedTimesheet.total_hours || 0;
    
    Object.values(breakAdjustments).forEach(minutes => {
      total -= (minutes / 60);
    });
    
    return Math.max(0, total).toFixed(2);
  };

  const getTotalBreakDeduction = () => {
    return Object.values(breakAdjustments).reduce((sum, mins) => sum + (parseInt(mins) || 0), 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'status-draft',
      submitted: 'status-submitted',
      approved: 'status-approved',
      rejected: 'status-rejected'
    };
    return <Badge className={styles[status] || 'status-draft'}>{status}</Badge>;
  };

  const isEditable = !myTimesheet || myTimesheet.status === 'draft' || myTimesheet.status === 'rejected';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Timesheets</h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Review and approve employee timesheets' : 'Log your hours for the pay period'}
          </p>
        </div>
        <Select 
          value={selectedPeriod?.id} 
          onValueChange={(id) => setSelectedPeriod(payPeriods.find(p => p.id === id))}
        >
          <SelectTrigger className="w-[250px]" data-testid="pay-period-select">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Select pay period" />
          </SelectTrigger>
          <SelectContent>
            {payPeriods.map((period) => (
              <SelectItem key={period.id} value={period.id}>
                {period.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isAdmin ? (
        /* Admin View - Review Timesheets */
        <Card>
          <CardHeader>
            <CardTitle>Employee Timesheets</CardTitle>
            <CardDescription>Review and approve submitted timesheets</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Total Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timesheets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      No timesheets for this pay period
                    </TableCell>
                  </TableRow>
                ) : (
                  timesheets.map((timesheet) => (
                    <TableRow key={timesheet.id} data-testid={`timesheet-row-${timesheet.id}`}>
                      <TableCell className="font-medium">{timesheet.user_name}</TableCell>
                      <TableCell>{timesheet.total_hours} hrs</TableCell>
                      <TableCell>{getStatusBadge(timesheet.status)}</TableCell>
                      <TableCell>
                        {timesheet.submitted_at 
                          ? format(parseISO(timesheet.submitted_at), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {timesheet.status === 'submitted' && (
                          <Button 
                            size="sm" 
                            onClick={() => openReviewDialog(timesheet)}
                            data-testid={`review-btn-${timesheet.id}`}
                          >
                            Review
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* Employee View - Enter Timesheet */
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-teal-600" />
                  Your Timesheet
                </CardTitle>
                <CardDescription>
                  {selectedPeriod && `${selectedPeriod.name}`}
                  {myTimesheet && (
                    <span className="ml-2">{getStatusBadge(myTimesheet.status)}</span>
                  )}
                </CardDescription>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Total Hours</p>
                <p className="text-2xl font-bold text-teal-600">{getTotalHours()}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {myTimesheet?.status === 'rejected' && myTimesheet.rejection_comment && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-700">Rejection Comment:</p>
                <p className="text-sm text-red-600">{myTimesheet.rejection_comment}</p>
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Day</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Break (min)</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow key={entry.date} data-testid={`entry-row-${entry.date}`}>
                      <TableCell className="font-medium">{entry.day}</TableCell>
                      <TableCell>{format(parseISO(entry.date), 'MMM d')}</TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={entry.start_time}
                          onChange={(e) => updateEntry(index, 'start_time', e.target.value)}
                          disabled={!isEditable}
                          className="w-28"
                          data-testid={`start-time-${entry.date}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={entry.end_time}
                          onChange={(e) => updateEntry(index, 'end_time', e.target.value)}
                          disabled={!isEditable}
                          className="w-28"
                          data-testid={`end-time-${entry.date}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={entry.break_minutes}
                          onChange={(e) => updateEntry(index, 'break_minutes', e.target.value)}
                          disabled={!isEditable}
                          className="w-20"
                          min="0"
                          data-testid={`break-${entry.date}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{entry.hours || '-'}</TableCell>
                      <TableCell>
                        <Input
                          value={entry.notes}
                          onChange={(e) => updateEntry(index, 'notes', e.target.value)}
                          disabled={!isEditable}
                          placeholder="Notes..."
                          className="w-32"
                          data-testid={`notes-${entry.date}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {isEditable && (
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Draft
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={handleSubmit}
                  disabled={saving}
                  data-testid="submit-timesheet-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Submit for Approval
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Review Timesheet</DialogTitle>
            <DialogDescription>
              {selectedTimesheet?.user_name} - Total: {selectedTimesheet?.total_hours} hours
              {getTotalBreakDeduction() > 0 && (
                <span className="ml-2 text-amber-600">
                  → Adjusted: {getAdjustedTotal()} hours
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTimesheet && (
            <div className="py-4">
              <div className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead>Break</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead className="text-amber-700">
                        <div className="flex items-center gap-1">
                          <Minus className="h-3 w-3" />
                          Deduct
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTimesheet.entries?.map((entry) => {
                      const deduction = breakAdjustments[entry.date] || 0;
                      const adjustedHours = Math.max(0, entry.hours - (deduction / 60));
                      return (
                        <TableRow key={entry.date}>
                          <TableCell>{format(parseISO(entry.date), 'EEE, MMM d')}</TableCell>
                          <TableCell>{entry.start_time}</TableCell>
                          <TableCell>{entry.end_time}</TableCell>
                          <TableCell>{entry.break_minutes}m</TableCell>
                          <TableCell className={deduction > 0 ? 'line-through text-slate-400' : 'font-medium'}>
                            {entry.hours}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="480"
                                step="5"
                                value={breakAdjustments[entry.date] || ''}
                                onChange={(e) => updateBreakAdjustment(entry.date, e.target.value)}
                                placeholder="0"
                                className="w-16 h-8 text-sm"
                                data-testid={`deduct-${entry.date}`}
                              />
                              <span className="text-xs text-slate-500">min</span>
                              {deduction > 0 && (
                                <span className="text-xs text-emerald-600 font-medium">
                                  → {adjustedHours.toFixed(2)}h
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Break Deduction Summary */}
              {getTotalBreakDeduction() > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-700">Break Deduction Summary</p>
                      <p className="text-sm text-amber-600 mt-1">
                        Total deduction: {getTotalBreakDeduction()} minutes ({(getTotalBreakDeduction() / 60).toFixed(2)} hours)
                      </p>
                      <p className="text-sm text-amber-600">
                        Original hours: {selectedTimesheet.total_hours} → Adjusted hours: {getAdjustedTotal()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-amber-700">Adjustment Notes (optional)</Label>
                    <Textarea
                      value={adjustmentNotes}
                      onChange={(e) => setAdjustmentNotes(e.target.value)}
                      placeholder="Reason for break deductions..."
                      className="mt-1"
                      data-testid="adjustment-notes"
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-2">
                <Label>Rejection Comment (required if rejecting)</Label>
                <Textarea
                  value={rejectionComment}
                  onChange={(e) => setRejectionComment(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  data-testid="rejection-comment"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleReject(selectedTimesheet?.id)}
              data-testid="reject-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleApprove(selectedTimesheet?.id)}
              disabled={approving}
              data-testid="approve-btn"
            >
              {approving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {getTotalBreakDeduction() > 0 ? 'Approve with Deductions' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
